import { Check } from "lucide-react";
import { Container, PageHero, PrimaryLink, OutlineLink, CtaBand } from "./_ui";
import { Seo } from "@/components/marketing/Seo";

const PILLARS = [
  { n: "01", title: "Sikkerhet", desc: "Kryptert lagring i Norge, to-faktor, SSO og BankID-signering. Bare styret ser styrets dokumenter." },
  { n: "02", title: "Sporbarhet", desc: "Hvem vedtok hva, og når? Hver signatur, transaksjon og endring har sin egen logglinje." },
  { n: "03", title: "Kontinuitet", desc: "Nye styremedlemmer er i gang samme dag. Historikken blir værende når noen går ut av styret." },
  { n: "04", title: "Etterlevelse", desc: "Bygget på aksjeloven: protokoll, aksjeeierbok, generalforsamling og meldinger til Altinn." },
];

const BADGES = ["BankID", "GDPR", "Norsk datalagring", "To-faktor", "SSO", "Revisjonslogg"];

const MEASURES = [
  "Kryptering av data både i transitt og ved lagring",
  "Tofaktorautentisering på alle kontoer",
  "Single sign-on (SSO) for konsern",
  "Detaljert tilgangsstyring per mappe og dokument",
  "Fullstendig tilgangs- og revisjonslogg",
  "GDPR-etterlevelse og databehandleravtale",
  "Jevnlige sikkerhetstester av ekstern part",
  "Backup og gjenoppretting med norsk driftsleverandør",
];

export default function Sikkerhet() {
  return (
    <>
      <Seo title="Sikkerhet og personvern" description="Kryptert lagring i Norge, BankID-signering, to-faktor, SSO og full tilgangslogg. Slik ivaretar Murphy Styrerom styrets dokumenter." path="/sikkerhet" />
      <PageHero label="Trygghet" title="Det du dokumenterer i dag skal stå seg om fem år.">
        Styredokumenter er blant de mest sensitive dataene et selskap har. Murphy Styrerom er bygget for at de skal være både trygge og tilgjengelige — også når styret byttes ut.
      </PageHero>

      <Container className="py-10">
        <div className="grid gap-5 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <div key={p.n} className="rounded-2xl border border-neutral-200 p-7">
              <span className="font-headings text-sm font-bold text-neutral-400">({p.n})</span>
              <h3 className="mt-3 font-headings text-xl font-bold text-neutral-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </Container>

      <Container className="py-6">
        <div className="flex flex-wrap gap-3">
          {BADGES.map((b) => (
            <span key={b} className="rounded-full bg-[#CDF546] px-4 py-2 text-xs font-semibold text-neutral-900">{b}</span>
          ))}
        </div>
      </Container>

      <Container className="py-12 sm:py-16">
        <h2 className="max-w-2xl font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Slik er styrerommet sikret.</h2>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {MEASURES.map((m) => (
            <li key={m} className="flex items-start gap-3 rounded-xl border border-neutral-200 p-4 text-sm text-neutral-700">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900" /> {m}
            </li>
          ))}
        </ul>
      </Container>

      <Container className="py-8 sm:py-12">
        <div className="rounded-2xl border border-neutral-200 p-8 sm:p-10">
          <h3 className="font-headings text-2xl font-bold tracking-tight text-neutral-900">Har sikkerhetsansvarlig spørsmål?</h3>
          <p className="mt-3 max-w-xl text-sm text-neutral-600">Vi stiller gjerne i et møte med IT- og sikkerhetsansvarlig før dere bestemmer dere.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <PrimaryLink to="/priser">Se priser</PrimaryLink>
            <OutlineLink href="mailto:post@murphy.no?subject=Sikkerhet%20-%20Murphy%20Styrerom">Book demo</OutlineLink>
          </div>
        </div>
      </Container>

      <CtaBand />
    </>
  );
}
